import { Fragment, useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import {
  Text,
  Icon,
  Image,
  Link,
  useToasts,
  useMediaQuery,
} from '@lounge-fe/ui-kit'
import type { BasketAddonItemProps } from './types'

import {
  GiftWrapPlugin,
  UnderwearCareBagPlugin,
} from '../../../../cart/plugins'
import { Money } from '@/components/Money'
import { useShopify } from '@/hooks/useShopify'
import { useCartPlugin, useCart } from '../../../..//cart/hooks'
import { useTranslation } from 'react-i18next'
import { sendClarityEvent } from '@/services/clarity'

const SUCCESS_DISPLAY_DURATION_MS = 800

export const BasketAddonItem: React.FC<BasketAddonItemProps> = ({
  product,
  onClose,
}) => {
  const { t } = useTranslation()
  const plugin = useCartPlugin()
  const cart = useCart()
  const { currency } = useShopify()
  const { toast } = useToasts()

  const [showSuccess, setShowSuccess] = useState(false)

  const isDesktop = useMediaQuery(({ screens }) => `(min-width: ${screens.sm})`)

  const setGiftWrap = useMutation({
    mutationFn: plugin(GiftWrapPlugin)?.setGiftWrap,
  })

  const addUnderwearCareBag = useMutation({
    mutationFn: plugin(UnderwearCareBagPlugin)?.addUnderwearCareBag,
  })

  const isGiftWrapProduct = useMemo(
    () => product.id === plugin(GiftWrapPlugin)?.getProduct().id,
    [product.id, plugin],
  )

  const isUnderwearCareBagProduct = useMemo(
    () => product.id === plugin(UnderwearCareBagPlugin)?.getProduct().id,
    [product.id, plugin],
  )

  const isGiftWrapEnabled = cart.data?.meta?.giftWrap ?? false
  const isPending = setGiftWrap.isPending || addUnderwearCareBag.isPending

  return (
    <div className="flex flex-col w-full gap-y-4 md:gap-y-6 py-8 px-5 md:px-6">
      <div
        className="flex items-center gap-x-1 cursor-pointer w-fit"
        onClick={onClose}
      >
        <Icon.ChevronLeft className="w-2 h-2" />
        <Link as="button" role="button" className="!typography--body-sm">
          {t('action.back')}
        </Link>
      </div>
      <div className="flex flex-row gap-x-4">
        <div className="aspect-square object-cover w-[100px] h-[100px] bg-surface-primary-default">
          {product.image ? (
            <Image
              className="object-cover w-full h-full aspect-square"
              data={{
                src: product.image.src,
                width: 100,
                height: 100,
                crop: 'center',
              }}
            />
          ) : (
            <div className="w-full h-full bg-surface-primary-default" />
          )}
        </div>
      </div>
      <div className="flex flex-col gap-y-1">
        <Text variant="title-six" className="text-base-primary font-regular">
          {product.title}
        </Text>
        {product.price && (
          <Text as="strong">
            <Money
              data={{
                amount: (product.price / 100).toString(),
                currencyCode: currency.active,
              }}
              as={Fragment}
            />
          </Text>
        )}

        <Link
          role="button"
          as="button"
          variant="body-md"
          className="flex items-center link w-fit"
          onClick={async () => {
            if (isPending) return

            try {
              let shouldShowSuccess = false

              if (isGiftWrapProduct) {
                const newGiftWrapState = !isGiftWrapEnabled
                await setGiftWrap.mutateAsync(newGiftWrapState)
                shouldShowSuccess = newGiftWrapState

                sendClarityEvent(
                  newGiftWrapState ? 'gift_wrap_added' : 'gift_wrap_removed',
                )
              } else if (isUnderwearCareBagProduct) {
                await addUnderwearCareBag.mutateAsync()
                shouldShowSuccess = true

                sendClarityEvent('underwear_care_bag_added')
              }

              if (shouldShowSuccess) {
                setShowSuccess(true)
                setTimeout(() => {
                  setShowSuccess(false)
                  onClose()
                }, SUCCESS_DISPLAY_DURATION_MS)
              } else {
                onClose()
              }
            } catch (e) {
              toast({
                severity: 'critical',
                title: t('general.error'),
                message: t('general.error_unexpected'),
                direction: isDesktop ? 'left' : 'top',
              })
            }
          }}
        >
          <div className="w-2 h-2 mr-2 flex items-center justify-center">
            {(() => {
              if (isPending) {
                return <Icon.Loading className="animate-spin w-2 h-2" />
              }

              if (showSuccess) {
                return <Icon.Check className="w-2 h-2" />
              }

              if (isGiftWrapProduct && isGiftWrapEnabled) {
                return <Icon.Trash className="w-3 h-3" />
              }

              if (!isGiftWrapProduct || !isGiftWrapEnabled) {
                return <Icon.Plus className="w-2 h-2" />
              }

              return null
            })()}
          </div>
          {isGiftWrapProduct && isGiftWrapEnabled
            ? t('action.remove')
            : t('product.add_to_bag')}
        </Link>
      </div>
    </div>
  )
}
